import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, SkipForward, ChevronLeft, CheckCircle2, Loader2 } from "lucide-react";
import { fillTemplate } from "@/lib/emailTemplates";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const BulkEmailStep = ({ p, index, total, tpl, vals, done, busy, onSend, onSkip, onBack }) => {
  const v = useMemo(() => ({ ...vals, business: p.name, contact: (p.contactPerson || "").split(" ")[0], bike_path: p.bikePathName || vals.bike_path }), [p, vals]);
  const [to, setTo] = useState(p.email || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  useEffect(() => {
    setTo(p.email || "");
    setSubject(fillTemplate(tpl.subject || "", v, false));
    setBody(fillTemplate(tpl.body || "", v, false));
  }, [p, tpl, v]);
  const valid = EMAIL_RE.test(to.trim());
  const mailto = `mailto:${encodeURIComponent(to.trim())}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div className="space-y-3" data-testid="bulk-email-step">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground tabular-nums" data-testid="bulk-email-progress">Email {index + 1} of {total}</div>
          <div className="font-semibold truncate" data-testid="bulk-email-business">{p.name}</div>
        </div>
        {done && <span className="text-xs text-emerald-400 flex items-center gap-1" data-testid="bulk-email-step-done"><CheckCircle2 className="w-4 h-4" />Marked as emailed</span>}
      </div>
      <div className="space-y-1.5"><Label>To</Label><Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Add this business's email address" data-testid="bulk-email-to" />
        {!valid && <p className="text-xs text-amber-400" data-testid="bulk-email-no-address">{to.trim() ? "That email address doesn't look right." : "No email on file for this prospect. Type one in (it's saved to the prospect) or skip it."}</p>}
      </div>
      <div className="space-y-1.5"><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="bulk-email-subject" /></div>
      <div className="space-y-1.5"><Label>Email</Label><Textarea rows={11} value={body} onChange={(e) => setBody(e.target.value)} className="text-sm leading-relaxed" data-testid="bulk-email-body" />
        {/\[[^\]\n]+\]/.test(body + subject) && <p className="text-xs text-amber-400" data-testid="bulk-email-placeholder-warning">Replace the [bracketed] text before sending.</p>}
      </div>
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
        <Button variant="ghost" onClick={onBack} disabled={index === 0 || busy} data-testid="bulk-email-back"><ChevronLeft className="w-4 h-4 mr-1" />Back</Button>
        <div className="flex flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={onSkip} disabled={busy} data-testid="bulk-email-skip"><SkipForward className="w-4 h-4 mr-2" />Skip</Button>
          <Button asChild={valid && !busy} disabled={!valid || busy} data-testid="bulk-email-send">
            {valid && !busy ? <a href={mailto} onClick={() => onSend(to.trim())}><Mail className="w-4 h-4 mr-2" />Open in email app &amp; mark emailed</a>
              : <span>{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin inline" /> : <Mail className="w-4 h-4 mr-2 inline" />}Open in email app &amp; mark emailed</span>}
          </Button>
        </div>
      </div>
    </div>
  );
};
